import { lazy, type ReactNode } from 'react';
import {
  Bookmark,
  ClipboardList,
  Gauge,
  History,
  Home,
  Layers,
  LayoutTemplate,
  Megaphone,
  Plus,
  Share2,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Tag,
  Upload,
  User,
} from 'lucide-react';

import type { Action } from '@/config/access';
import {
  BRAND_CREATE_PATH,
  BRAND_PATTERN,
  BRAND_TAB_PATTERN,
  BRANDS_PATH,
  CAMPAIGN_CREATE_PATH,
  CAMPAIGNS_PATH,
  type CampaignSection,
  DASHBOARD_PATH,
  PRODUCT_IMPORT_PATTERN,
  PRODUCT_IMPORTS_PATTERN,
  SAVED_ELEMENTS_PATH,
  SETTINGS_PATH,
  TEMPLATE_CREATE_PATH,
  TEMPLATE_EDITOR_PATTERN,
  TEMPLATE_PATTERN,
  TEMPLATES_PATH,
} from '@/config/paths';
import type { TranslationKey } from '@/i18n';
import { ICON_SIZE } from '@/components/ui/iconSize';

// Each page is one chunk. The app gets the chunk the first time it shows the route.
const CampaignSectionStub = lazy(() => import('@/pages/campaigns/CampaignSectionStub').then((module) => ({ default: module.CampaignSectionStub })));
const BrandCreatePage = lazy(() => import('@/pages/brandHub/brand/BrandCreatePage').then((module) => ({ default: module.BrandCreatePage })));
const BrandPage = lazy(() => import('@/pages/brandHub/brand/BrandPage').then((module) => ({ default: module.BrandPage })));
const BrandsPage = lazy(() => import('@/pages/brandHub/brand/BrandsPage').then((module) => ({ default: module.BrandsPage })));
const CampaignBriefingPage = lazy(() => import('@/pages/campaigns/campaign/CampaignBriefingPage').then((module) => ({ default: module.CampaignBriefingPage })));
const CampaignsPage = lazy(() => import('@/pages/campaigns/campaign/CampaignsPage').then((module) => ({ default: module.CampaignsPage })));
const DashboardPage = lazy(() => import('@/pages/DashboardPage').then((module) => ({ default: module.DashboardPage })));
const ProductImportPage = lazy(() => import('@/pages/brandHub/brand/ProductImportPage').then((module) => ({ default: module.ProductImportPage })));
const ProductImportsPage = lazy(() => import('@/pages/brandHub/brand/ProductImportsPage').then((module) => ({ default: module.ProductImportsPage })));
const SavedElementsPage = lazy(() => import('@/pages/brandHub/savedElements/SavedElementsPage').then((module) => ({ default: module.SavedElementsPage })));
const SettingsPage = lazy(() => import('@/pages/SettingsPage').then((module) => ({ default: module.SettingsPage })));
const TemplateFormPage = lazy(() => import('@/pages/brandHub/templates/TemplateFormPage').then((module) => ({ default: module.TemplateFormPage })));
const TemplatesPage = lazy(() => import('@/pages/brandHub/templates/TemplatesPage').then((module) => ({ default: module.TemplatesPage })));
const TemplateEditorPage = lazy(() => import('@/pages/brandHub/templates/TemplateEditorPage').then((module) => ({ default: module.TemplateEditorPage })));

/** The headings that add live data to the title and subtitle keys of their row. */
export const LIVE_HEADINGS = [
  'dashboardDate',
  'campaignCount',
  'campaignName',
  'brandCreateStep',
  'brand',
  'productBrand',
  'templateName',
  'sessionUser',
] as const;
export type LiveHeading = (typeof LIVE_HEADINGS)[number];

export interface Crumb {
  labelKey: TranslationKey;
  to: string;
}

export interface AppRoute {
  path: string;
  end?: boolean;
  labelKey: TranslationKey;
  titleKey: TranslationKey;
  subtitleKey?: TranslationKey;
  icon: ReactNode;
  element: ReactNode;
  /** Gated by `ACCESS` in config/access.ts. No action means everyone. */
  action?: Action;
  /** Shows the route outside the shell: no sidebar, no top bar and no page heading. */
  chromeless?: boolean;
  /** The back link above the page heading. */
  crumb?: Crumb;
  liveHeading?: LiveHeading;
}

/** The `handle` of each route object. The shell reads the heading from it. */
export interface RouteHandle {
  route: AppRoute;
}

const CAMPAIGNS_CRUMB: Crumb = { labelKey: 'nav.campaigns', to: CAMPAIGNS_PATH };
const ALL_CAMPAIGNS_CRUMB: Crumb = { labelKey: 'campaigns.allCampaigns', to: CAMPAIGNS_PATH };
const BRANDS_CRUMB: Crumb = { labelKey: 'nav.brands', to: BRANDS_PATH };
const TEMPLATES_CRUMB: Crumb = { labelKey: 'nav.templates', to: TEMPLATES_PATH };

export const WORKSPACE_ROUTES: AppRoute[] = [
  {
    path: DASHBOARD_PATH,
    end: true,
    labelKey: 'nav.dashboard',
    titleKey: 'dashboard.title',
    icon: <Gauge size={ICON_SIZE.md} />,
    element: <DashboardPage />,
    liveHeading: 'dashboardDate',
  },
  {
    path: CAMPAIGNS_PATH,
    end: true,
    labelKey: 'nav.campaigns',
    titleKey: 'campaigns.title',
    icon: <Megaphone size={ICON_SIZE.md} />,
    element: <CampaignsPage />,
    action: 'campaignsPage',
    liveHeading: 'campaignCount',
  },
];

export const BRAND_HUB_ROUTES: AppRoute[] = [
  {
    path: BRANDS_PATH,
    end: true,
    labelKey: 'nav.brands',
    titleKey: 'brandHub.brands.title',
    subtitleKey: 'brandHub.brands.subtitle',
    icon: <Tag size={ICON_SIZE.md} />,
    element: <BrandsPage />,
    action: 'brandsPage',
  },
  {
    path: TEMPLATES_PATH,
    end: true,
    labelKey: 'nav.templates',
    titleKey: 'brandHub.template.title',
    subtitleKey: 'brandHub.template.subtitle',
    icon: <LayoutTemplate size={ICON_SIZE.md} />,
    element: <TemplatesPage />,
    action: 'templatesPage',
  },
  {
    path: SAVED_ELEMENTS_PATH,
    labelKey: 'nav.savedElements',
    titleKey: 'brandHub.savedElement.title',
    subtitleKey: 'brandHub.savedElement.subtitle',
    icon: <Bookmark size={ICON_SIZE.md} />,
    element: <SavedElementsPage />,
    action: 'savedElementsPage',
  },
];

export interface CampaignSectionRoute extends AppRoute {
  path: CampaignSection;
  groupLabelKey?: TranslationKey;
}

/** The eight steps of a campaign, in the order the design walks them. Only the
 *  brief is built; every other step says so. */
export const CAMPAIGN_SECTION_ROUTES: CampaignSectionRoute[] = [
  {
    path: 'overview',
    labelKey: 'nav.section.overview',
    titleKey: 'campaign.overview.title',
    icon: <Home size={ICON_SIZE.md} />,
    element: <CampaignSectionStub titleKey="campaign.overview.title" />,
    action: 'campaignsPage',
    crumb: ALL_CAMPAIGNS_CRUMB,
    liveHeading: 'campaignName',
  },
  {
    path: 'brief',
    labelKey: 'nav.section.brief',
    titleKey: 'campaignBriefing.title',
    icon: <ClipboardList size={ICON_SIZE.md} />,
    element: <CampaignBriefingPage />,
    action: 'campaignsPage',
    crumb: ALL_CAMPAIGNS_CRUMB,
    liveHeading: 'campaignName',
  },
  {
    path: 'platforms',
    labelKey: 'nav.section.platforms',
    titleKey: 'campaign.platforms.title',
    icon: <Share2 size={ICON_SIZE.md} />,
    element: <CampaignSectionStub titleKey="campaign.platforms.title" />,
    action: 'campaignsPage',
    crumb: ALL_CAMPAIGNS_CRUMB,
    liveHeading: 'campaignName',
  },
  {
    path: 'create',
    labelKey: 'nav.section.create',
    titleKey: 'campaign.create.title',
    icon: <Sparkles size={ICON_SIZE.md} />,
    element: <CampaignSectionStub titleKey="campaign.create.title" />,
    action: 'campaignsPage',
    crumb: ALL_CAMPAIGNS_CRUMB,
    liveHeading: 'campaignName',
  },
  {
    path: 'quality',
    labelKey: 'nav.section.quality',
    titleKey: 'campaign.quality.title',
    icon: <ShieldCheck size={ICON_SIZE.md} />,
    element: <CampaignSectionStub titleKey="campaign.quality.title" />,
    action: 'campaignsPage',
    crumb: ALL_CAMPAIGNS_CRUMB,
    liveHeading: 'campaignName',
  },
  {
    path: 'setup',
    labelKey: 'nav.section.setup',
    titleKey: 'campaign.setup.title',
    icon: <SlidersHorizontal size={ICON_SIZE.md} />,
    element: <CampaignSectionStub titleKey="campaign.setup.title" />,
    action: 'campaignsPage',
    crumb: ALL_CAMPAIGNS_CRUMB,
    liveHeading: 'campaignName',
  },
  {
    path: 'publishing',
    labelKey: 'nav.section.publishing',
    titleKey: 'campaign.publishing.title',
    icon: <Upload size={ICON_SIZE.md} />,
    element: <CampaignSectionStub titleKey="campaign.publishing.title" />,
    action: 'campaignsPage',
    crumb: ALL_CAMPAIGNS_CRUMB,
    liveHeading: 'campaignName',
  },
  {
    path: 'library',
    groupLabelKey: 'nav.section.operations',
    labelKey: 'nav.section.library',
    titleKey: 'campaign.library.title',
    icon: <Layers size={ICON_SIZE.md} />,
    element: <CampaignSectionStub titleKey="campaign.library.title" />,
    action: 'campaignsPage',
    crumb: ALL_CAMPAIGNS_CRUMB,
    liveHeading: 'campaignName',
  },
];

export const UNLISTED_ROUTES: AppRoute[] = [
  {
    path: CAMPAIGN_CREATE_PATH,
    labelKey: 'campaigns.create',
    titleKey: 'campaignBriefing.createTitle',
    subtitleKey: 'campaignBriefing.title',
    icon: <Megaphone size={ICON_SIZE.md} />,
    element: <CampaignBriefingPage />,
    action: 'campaignManage',
    crumb: CAMPAIGNS_CRUMB,
  },
  {
    path: BRAND_CREATE_PATH,
    labelKey: 'brandHub.brandCreate.title',
    titleKey: 'brandHub.brandCreate.title',
    icon: <Plus size={ICON_SIZE.md} />,
    element: <BrandCreatePage />,
    action: 'brandManage',
    crumb: BRANDS_CRUMB,
    liveHeading: 'brandCreateStep',
  },
  {
    path: BRAND_PATTERN,
    labelKey: 'brandHub.brand.title',
    titleKey: 'brandHub.brand.title',
    icon: <Tag size={ICON_SIZE.md} />,
    element: <BrandPage />,
    action: 'brandsPage',
    crumb: BRANDS_CRUMB,
    liveHeading: 'brand',
  },
  {
    path: BRAND_TAB_PATTERN,
    labelKey: 'brandHub.brand.title',
    titleKey: 'brandHub.brand.title',
    icon: <Tag size={ICON_SIZE.md} />,
    element: <BrandPage />,
    action: 'brandsPage',
    crumb: BRANDS_CRUMB,
    liveHeading: 'brand',
  },
  {
    path: PRODUCT_IMPORT_PATTERN,
    labelKey: 'brandHub.import.title',
    titleKey: 'brandHub.import.title',
    icon: <Upload size={ICON_SIZE.md} />,
    element: <ProductImportPage />,
    action: 'brandManage',
    liveHeading: 'productBrand',
  },
  {
    path: PRODUCT_IMPORTS_PATTERN,
    labelKey: 'brandHub.import.history.title',
    titleKey: 'brandHub.import.history.title',
    icon: <History size={ICON_SIZE.md} />,
    element: <ProductImportsPage />,
    action: 'brandsPage',
    liveHeading: 'productBrand',
  },
  {
    path: TEMPLATE_CREATE_PATH,
    labelKey: 'brandHub.template.createTitle',
    titleKey: 'brandHub.template.createTitle',
    subtitleKey: 'brandHub.template.addHint',
    icon: <LayoutTemplate size={ICON_SIZE.md} />,
    element: <TemplateFormPage />,
    action: 'templateCreate',
    crumb: TEMPLATES_CRUMB,
  },
  {
    path: TEMPLATE_PATTERN,
    labelKey: 'brandHub.template.editTitle',
    titleKey: 'brandHub.template.editTitle',
    icon: <LayoutTemplate size={ICON_SIZE.md} />,
    element: <TemplateFormPage />,
    action: 'templatesPage',
    crumb: TEMPLATES_CRUMB,
    liveHeading: 'templateName',
  },
  {
    path: TEMPLATE_EDITOR_PATTERN,
    labelKey: 'brandHub.editor.title',
    titleKey: 'brandHub.editor.title',
    icon: <LayoutTemplate size={ICON_SIZE.md} />,
    element: <TemplateEditorPage />,
    action: 'templateUpdate',
    chromeless: true,
  },
  {
    path: SETTINGS_PATH,
    labelKey: 'nav.settings',
    titleKey: 'settings.title',
    icon: <User size={ICON_SIZE.md} />,
    element: <SettingsPage />,
    liveHeading: 'sessionUser',
  },
];

export const DEFAULT_ROUTE = CAMPAIGNS_PATH;

export const NAV_GROUPS = [
  { key: 'brandHub', labelKey: 'nav.group.brandHub', routes: BRAND_HUB_ROUTES },
  { key: 'workspace', labelKey: 'nav.group.workspace', routes: WORKSPACE_ROUTES },
] as const;
